import { useEffect, useState } from 'react';
import {
  restoreAvatarFactoryVersion,
  updateAvatarFactoryDraft,
  type AvatarFactoryLayer,
  type AvatarFactoryLayerKey,
} from '../../lib/admin-avatar-factory-api';
import type { AutosaveState, AvatarFactoryChildProps } from './types';

const AUTOSAVE_DEBOUNCE_MS = 700;
const LAYER_ORDER: AvatarFactoryLayerKey[] = ['soul', 'style', 'memory', 'skill'];

type LayerValueMap = Record<AvatarFactoryLayerKey, string>;

function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return fallback;
}

function getLayerMap(layers: AvatarFactoryLayer[]): Record<AvatarFactoryLayerKey, AvatarFactoryLayer | null> {
  return {
    soul: layers.find((layer) => layer.layerKey === 'soul') ?? null,
    style: layers.find((layer) => layer.layerKey === 'style') ?? null,
    memory: layers.find((layer) => layer.layerKey === 'memory') ?? null,
    skill: layers.find((layer) => layer.layerKey === 'skill') ?? null,
  };
}

function buildLayerValues(layers: AvatarFactoryLayer[]): LayerValueMap {
  const layerMap = getLayerMap(layers);
  return {
    soul: layerMap.soul?.content ?? '',
    style: layerMap.style?.content ?? '',
    memory: layerMap.memory?.content ?? '',
    skill: layerMap.skill?.content ?? '',
  };
}

function getEvidenceLabel(item: Record<string, unknown>, fallbackIndex: number): string {
  const filename =
    (typeof item.filename === 'string' && item.filename) ||
    (typeof item.file_name === 'string' && item.file_name) ||
    (typeof item.source_filename === 'string' && item.source_filename) ||
    'evidence';
  const chunkIndexRaw = item.chunk_index ?? item.chunkIndex ?? item.source_chunk_index ?? fallbackIndex;
  const chunkIndex = typeof chunkIndexRaw === 'number' ? chunkIndexRaw : Number(chunkIndexRaw);
  const safeChunk = Number.isFinite(chunkIndex) ? chunkIndex : fallbackIndex;
  return `${filename} chunk ${safeChunk}`;
}

export default function IdentityEditorTab({ accessToken, copy, draft, onDraftChange, onError }: AvatarFactoryChildProps) {
  const [layerValues, setLayerValues] = useState<LayerValueMap>(() => buildLayerValues(draft.layers));
  const [activeLayerKey, setActiveLayerKey] = useState<AvatarFactoryLayerKey>('soul');
  const [pendingLayerEdit, setPendingLayerEdit] = useState<{
    layerKey: AvatarFactoryLayerKey;
    value: string;
  } | null>(null);
  const [autosaveState, setAutosaveState] = useState<AutosaveState>('idle');
  const [versionMessage, setVersionMessage] = useState('');

  useEffect(() => {
    setLayerValues(buildLayerValues(draft.layers));
  }, [draft.id, draft.layers]);

  useEffect(() => {
    if (!pendingLayerEdit) {
      return;
    }

    setAutosaveState('saving');
    let cancelled = false;

    const timer = window.setTimeout(() => {
      void updateAvatarFactoryDraft(accessToken, draft.id, {
        layers: {
          [pendingLayerEdit.layerKey]: pendingLayerEdit.value,
        },
      })
        .then((nextDraft) => {
          if (cancelled) {
            return;
          }
          onDraftChange(nextDraft);
          setAutosaveState('saved');
          setPendingLayerEdit(null);
        })
        .catch((error) => {
          if (cancelled) {
            return;
          }
          setAutosaveState('failed');
          setPendingLayerEdit(null);
          onError(getErrorMessage(error, copy.identity.autosaveFallback));
        });
    }, AUTOSAVE_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [accessToken, copy.identity.autosaveFallback, draft.id, onDraftChange, onError, pendingLayerEdit]);

  const layerMap = getLayerMap(draft.layers);
  const activeLayer = layerMap[activeLayerKey];
  const activeLayerLabel = copy.identity.layers[activeLayerKey];

  return (
    <div className="avatar-factory-identity-panel">
      <div className="avatar-factory-section-heading">
        <p className="avatar-factory-panel-kicker">{copy.identity.eyebrow}</p>
        <h2>{copy.identity.title}</h2>
        <span>
          {autosaveState === 'saving'
            ? copy.common.autosaveSaving
            : autosaveState === 'saved'
              ? copy.common.autosaveSaved
              : copy.identity.editable}
        </span>
      </div>

      <div className="avatar-factory-layer-tabs" role="tablist" aria-label={copy.identity.title}>
        {LAYER_ORDER.map((layerKey) => {
          const label = copy.identity.layers[layerKey];
          const isActive = activeLayerKey === layerKey;
          return (
            <button
              key={layerKey}
              id={`avatar-factory-layer-tab-${layerKey}`}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={`avatar-factory-layer-panel-${layerKey}`}
              className={isActive ? 'is-active' : ''}
              onClick={() => setActiveLayerKey(layerKey)}
            >
              {label}
            </button>
          );
        })}
      </div>

      <section
        id={`avatar-factory-layer-panel-${activeLayerKey}`}
        className="avatar-factory-layer-card"
        role="tabpanel"
        aria-label={`${activeLayerLabel} content`}
      >
        <div className="avatar-factory-card-heading">
          <h3>{activeLayerLabel}</h3>
          <span>{activeLayer?.content ? copy.identity.generated : copy.common.empty}</span>
        </div>
        <div className="avatar-factory-field">
          <textarea
            aria-label={activeLayerLabel}
            rows={8}
            value={layerValues[activeLayerKey]}
            onChange={(event) => {
              const nextValue = event.target.value;
              setLayerValues((previous) => ({
                ...previous,
                [activeLayerKey]: nextValue,
              }));
              setPendingLayerEdit({ layerKey: activeLayerKey, value: nextValue });
            }}
          />
        </div>

        <div className="avatar-factory-layer-support">
          <div>
            <p>{copy.identity.evidence}</p>
            {activeLayer && activeLayer.evidenceJson.length > 0 ? (
              <div className="avatar-factory-evidence-list">
                {activeLayer.evidenceJson.map((item, index) => {
                  const labelText = getEvidenceLabel(item, index);
                  return (
                    <button key={`${activeLayerKey}-evidence-${index}`} type="button">
                      {labelText}
                    </button>
                  );
                })}
              </div>
            ) : (
              <p>{copy.identity.noEvidence}</p>
            )}
          </div>

          <div>
            <p>{copy.identity.reviewNotes}</p>
            {activeLayer && activeLayer.reviewNotesJson.length > 0 ? (
              <ul>
                {activeLayer.reviewNotesJson.map((note, index) => (
                  <li key={`${activeLayerKey}-note-${index}`}>{note}</li>
                ))}
              </ul>
            ) : (
              <p>{copy.identity.noReviewNotes}</p>
            )}
          </div>
        </div>
      </section>

      <section className="avatar-factory-card avatar-factory-versions-card">
        <div className="avatar-factory-card-heading">
          <h3>{copy.common.versions}</h3>
          <span>{copy.common.versionsSaved(draft.versions.length)}</span>
        </div>
        {draft.versions.length > 0 ? (
          <ul>
            {draft.versions.map((version) => (
              <li key={version.id}>
                <span>{version.name}</span>{' '}
                <button
                  type="button"
                  onClick={() => {
                    setVersionMessage('');
                    void restoreAvatarFactoryVersion(accessToken, draft.id, version.id)
                      .then((nextDraft) => {
                        onDraftChange(nextDraft);
                        setVersionMessage(copy.common.versionRestored);
                      })
                      .catch((error) => {
                        onError(getErrorMessage(error, copy.identity.restoreFallback));
                      });
                  }}
                >
                  {copy.common.restoreVersion(version.name)}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p>{copy.common.noSavedVersions}</p>
        )}
        {versionMessage ? <p>{versionMessage}</p> : null}
      </section>

      <p className="avatar-factory-autosave" aria-live="polite">
        {autosaveState === 'saving' ? copy.common.saving : null}
        {autosaveState === 'saved' ? copy.common.saved : null}
        {autosaveState === 'failed' ? copy.common.saveFailed : null}
      </p>
    </div>
  );
}
